import type { IconName } from '@/components/ui';

/**
 * The application's module registry.
 *
 * Navigation, route titles and the "coming soon" placeholders are all driven
 * from this one list. Adding a future ELA Lab module — analytics, comparison,
 * protocols, admin — means adding an entry here and a route in `routes.tsx`;
 * no navigation component needs to change.
 */
export interface AppModule {
  id: string;
  label: string;
  path: string;
  icon: IconName;
  /** Grouping in the sidebar. */
  section: 'workspace' | 'analysis' | 'account';
  /** False while the module is a signposted placeholder. */
  available: boolean;
  description: string;
  /** Matches child routes too, e.g. /sessions/:id highlights "Sessions". */
  matchPrefix?: boolean;
}

export const appModules: readonly AppModule[] = [
  {
    id: 'overview',
    label: 'Overview',
    path: '/',
    icon: 'grid',
    section: 'workspace',
    available: true,
    description: 'Recording activity and headline metrics across your sessions.',
  },
  {
    id: 'sessions',
    label: 'Sessions',
    path: '/sessions',
    icon: 'list',
    section: 'workspace',
    available: true,
    matchPrefix: true,
    description: 'Every meditation session recorded with your ela headband.',
  },
  {
    id: 'mindos',
    label: 'MindOS Live',
    path: '/mindos',
    icon: 'waves',
    section: 'workspace',
    available: true,
    description: 'Real-time 3-D brain view streamed from your ela headband over Bluetooth.',
  },
  {
    id: 'analytics',
    label: 'EEG analytics',
    path: '/analytics',
    icon: 'activity',
    section: 'analysis',
    available: true,
    description: 'Aggregate signal statistics derived from your recorded sessions.',
  },
  {
    id: 'compare',
    label: 'Comparison',
    path: '/compare',
    icon: 'compare',
    section: 'analysis',
    available: false,
    description: 'Place two or more sessions side by side and diff their signal profiles.',
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: 'file',
    section: 'analysis',
    available: false,
    description: 'Generated meditation reports you can share or archive.',
  },
  {
    id: 'protocols',
    label: 'Protocols',
    path: '/protocols',
    icon: 'flask',
    section: 'analysis',
    available: false,
    description: 'Define and run experimental protocols across cohorts of sessions.',
  },
  {
    id: 'profile',
    label: 'Profile',
    path: '/profile',
    icon: 'user',
    section: 'account',
    available: true,
    description: 'Your ELA Lab account and active session.',
  },
];

export const moduleSections: readonly { id: AppModule['section']; label: string }[] = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'account', label: 'Account' },
];

export function modulesInSection(section: AppModule['section']): readonly AppModule[] {
  return appModules.filter((module) => module.section === section);
}

export function findModuleByPath(pathname: string): AppModule | undefined {
  // Longest match wins so /sessions/:id resolves to Sessions, not Overview.
  return [...appModules]
    .sort((a, b) => b.path.length - a.path.length)
    .find((module) =>
      module.path === '/' ? pathname === '/' : pathname === module.path || pathname.startsWith(`${module.path}/`),
    );
}
